import type { CoverageKind, EligibilityResult } from '~/lib/eligibility-check';
import type { Cart, CartInsurance } from '~/types/cart';

/**
 * Flag for the greenhouse-style D2C ordering sequence: patient demographics and
 * insurance are collected before the catalog is shown, and the catalog is then
 * filtered to the patient's state and carrier.
 *
 * ON BY DEFAULT on this prototype branch so the deploy preview renders the new
 * flow — `import.meta.env` is inlined at build time and the PR image is built
 * without build args, so an opt-in flag would always resolve to off there.
 *
 * Set `VITE_D2C_INTAKE_FIRST=false` to get the original flow back. This default
 * MUST be inverted back to opt-in before any merge to main.
 */
export function isIntakeFirstEnabled(): boolean {
  return import.meta.env.VITE_D2C_INTAKE_FIRST !== 'false';
}

/** Carrier segment the marketplace catalog endpoints expect. */
type CatalogCarrier = 'Medicare' | 'Medicaid';

/**
 * The insurance coverage flag stores carriers on `insurances`; the legacy flow
 * stores a single selected carrier on `insurance`.
 */
export function getIntakeInsurances(cart: Cart): CartInsurance[] {
  if (cart.insurances?.length) return cart.insurances;
  return cart.insurance?.uuid ? [cart.insurance] : [];
}

export function hasIntakePatient(cart: Cart): boolean {
  const patient = cart.patient;
  return Boolean(patient?.firstName && patient.lastName && patient.dob && patient.address?.state);
}

export function isIntakeComplete(cart: Cart): boolean {
  return hasIntakePatient(cart) && getIntakeInsurances(cart).length > 0;
}

/**
 * The patient's own address drives the catalog once intake runs first, falling
 * back to the cart's coverage state before demographics are captured.
 */
export function getIntakeState(cart: Cart): string {
  return cart.patient?.address?.state || cart.state;
}

/**
 * A single carrier narrows the catalog to that carrier's covered products.
 * Mixed coverage (Medicare plus Medicaid) returns undefined so the catalog
 * returns every carrier the state serves, matching the multi-carrier behaviour.
 */
export function getIntakeCarrier(cart: Cart): CatalogCarrier | undefined {
  const carriers = new Set(
    getIntakeInsurances(cart).map((insurance) =>
      toCatalogCarrier(insurance.carrierType ?? insurance.name)
    )
  );

  return carriers.size === 1 ? [...carriers][0] : undefined;
}

/** Every catalog carrier the patient has entered, in the catalog's vocabulary. */
export function getIntakeCarriers(cart: Cart): CatalogCarrier[] {
  return [
    ...new Set(
      getIntakeInsurances(cart).map((insurance) =>
        toCatalogCarrier(insurance.carrierType ?? insurance.name)
      )
    ),
  ];
}

export type ItemCoverage = { covered: true } | { covered: false; needs: string | undefined };

/**
 * Whether one of the patient's carriers covers an item, and if not, which
 * carrier would. An item covered by no carrier is simply not covered; when the
 * catalog returns no carrier data at all, the caller should skip locking.
 */
export function getItemCoverage(coveredBy: string[], patientCarriers: string[]): ItemCoverage {
  if (coveredBy.length === 0) return { covered: false, needs: undefined };
  if (coveredBy.some((carrier) => patientCarriers.includes(carrier))) return { covered: true };
  return {
    covered: false,
    needs: coveredBy.find((carrier) => !patientCarriers.includes(carrier)),
  };
}

function toCatalogCarrier(value: string | undefined): CatalogCarrier {
  const lower = value?.toLowerCase();
  return lower === 'medicare' || lower === 'medicare_advantage' ? 'Medicare' : 'Medicaid';
}

/**
 * Where each plan stands for the catalog. Only verified or not-yet-checked
 * plans unlock items; a plan that came back empty, or was never collected, is
 * missing coverage the patient may not have.
 */
export type CoverageStanding =
  | 'verified'
  | 'unverified'
  | 'not_found'
  | 'plan_details_needed'
  | 'not_collected'
  | 'not_enrolled'
  // The other plan's check found this one too, but it has not been added.
  | 'discovered';

export type CoverageStatus = {
  coverage: CoverageKind;
  carrier: CatalogCarrier;
  standing: CoverageStanding;
};

const COVERAGE_CARRIER: Record<CoverageKind, CatalogCarrier> = {
  medicare: 'Medicare',
  medicaid: 'Medicaid',
};

export function getCoverageStatuses(
  cart: Cart,
  results: Partial<Record<CoverageKind, EligibilityResult>>
): CoverageStatus[] {
  return (['medicare', 'medicaid'] as const).map((coverage) => {
    const carrier = COVERAGE_CARRIER[coverage];
    const insurance = getIntakeInsurances(cart).find(
      (entry) => toCatalogCarrier(entry.carrierType ?? entry.name) === carrier
    );
    const selection =
      coverage === 'medicare' ? cart.medicareCoverage?.selection : cart.medicaidCoverage?.selection;

    if (!insurance) {
      if (isDiscoveredByOtherCheck(cart, results, coverage)) {
        return { coverage, carrier, standing: 'discovered' };
      }
      return {
        coverage,
        carrier,
        standing: selection === 'no' ? 'not_enrolled' : 'not_collected',
      };
    }

    const result = results[coverage];
    // A result only speaks for the ID it was run against.
    if (!result || result.memberId !== insurance.memberId.trim()) {
      return { coverage, carrier, standing: 'unverified' };
    }

    const standing: CoverageStanding =
      result.status === 'found'
        ? 'verified'
        : result.status === 'not_found'
          ? 'not_found'
          : 'plan_details_needed';
    return { coverage, carrier, standing };
  });
}

/** Carriers that should unlock items: confirmed, or entered and awaiting a check. */
export function getUsableCarriers(statuses: CoverageStatus[]): CatalogCarrier[] {
  return statuses
    .filter((status) => status.standing === 'verified' || status.standing === 'unverified')
    .map((status) => status.carrier);
}

/** Plans the patient may have but we could not confirm; worth warning about. */
export function getMissingCoverage(statuses: CoverageStatus[]): CoverageStatus[] {
  return statuses.filter(
    (status) =>
      status.standing === 'not_found' ||
      status.standing === 'plan_details_needed' ||
      status.standing === 'not_collected' ||
      status.standing === 'discovered'
  );
}

function isDiscoveredByOtherCheck(
  cart: Cart,
  results: Partial<Record<CoverageKind, EligibilityResult>>,
  coverage: CoverageKind
) {
  const other: CoverageKind = coverage === 'medicare' ? 'medicaid' : 'medicare';
  const otherInsurance = getIntakeInsurances(cart).find(
    (entry) => toCatalogCarrier(entry.carrierType ?? entry.name) === COVERAGE_CARRIER[other]
  );
  const result = results[other];
  // Only trust a discovery made against the ID that is still entered.
  return Boolean(
    otherInsurance &&
    result?.status === 'found' &&
    result.memberId === otherInsurance.memberId.trim() &&
    result.additionalPlan?.coverage === coverage
  );
}

export type CartItemCoverage =
  | { productId: string; status: 'covered'; carriers: string[] }
  | { productId: string; status: 'not_covered'; needs: string | undefined }
  // Added before its coverage was noted; Review cannot judge it.
  | { productId: string; status: 'unknown' };

/**
 * Checks each item against the coverage confirmed on the insurance step. Items
 * are picked while discovery is still running, against self-reported coverage,
 * so some may turn out not to be covered by what discovery actually found.
 */
export function getCartItemsCoverage(
  cart: Cart,
  coveredByProduct: Record<string, string[]>,
  /** Carriers covered some other way, such as a card photo awaiting its ID. */
  extraCarriers: string[] = []
): CartItemCoverage[] {
  const patientCarriers: string[] = [...new Set([...getIntakeCarriers(cart), ...extraCarriers])];

  return cart.items.map(({ product }): CartItemCoverage => {
    const coveredBy = coveredByProduct[product.id];
    if (!coveredBy) return { productId: product.id, status: 'unknown' };

    const coverage = getItemCoverage(coveredBy, patientCarriers);
    return coverage.covered
      ? {
          productId: product.id,
          status: 'covered',
          carriers: coveredBy.filter((carrier) => patientCarriers.includes(carrier)),
        }
      : { productId: product.id, status: 'not_covered', needs: coverage.needs };
  });
}
